import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import {
  useUpdateInvoiceCardFee,
  useUpdateSourceCardFee,
  type SourceCardFee,
  type SourceCardFeeUpdate,
} from "@/api/hooks";
import {
  InlineFieldGroup,
  type InlineGroupField,
} from "@/components/inline-field-group";
import { cn } from "@/lib/utils";

type CardFeeKey = "rule" | "overAmount" | "percent";
type Rule = "unknown" | NonNullable<SourceCardFee["rule"]>;

const RULE_OPTIONS: Array<{ value: Rule; label: string }> = [
  { value: "unknown", label: "Not known" },
  { value: "none", label: "No card fee" },
  { value: "always", label: "Fee on every card payment" },
  { value: "over_amount", label: "Fee only over an amount" },
];

const FIELDS: ReadonlyArray<InlineGroupField<CardFeeKey>> = [
  { key: "rule", label: "Policy", kind: "select", options: RULE_OPTIONS },
  {
    key: "overAmount",
    label: "Fee over",
    required: true,
    inputMode: "decimal",
    className: "col-span-3",
    hidden: (draft) => draft.rule !== "over_amount",
  },
  {
    key: "percent",
    label: "Rate",
    inputMode: "decimal",
    suffix: "%",
    className: "col-span-3",
    hidden: (draft) => draft.rule === "unknown" || draft.rule === "none",
  },
];

function plainNumber(value: string | null | undefined) {
  return value == null ? "" : String(Number(value));
}

function draftOf(fee: SourceCardFee): Record<CardFeeKey, string> {
  return {
    rule: fee.rule ?? "unknown",
    overAmount: plainNumber(fee.fee_over_amount),
    percent: plainNumber(fee.fee_percent),
  };
}

function number(raw: string) {
  const trimmed = raw.replace(/[$,%\s]/g, "");
  return trimmed === "" ? null : Number(trimmed);
}

function invalidFields(draft: Record<CardFeeKey, string>): CardFeeKey[] {
  const bad: CardFeeKey[] = [];
  const over = number(draft.overAmount);
  const percent = number(draft.percent);
  if (
    draft.rule === "over_amount" &&
    (over === null || !Number.isFinite(over) || over <= 0)
  )
    bad.push("overAmount");
  if (
    percent !== null &&
    (!Number.isFinite(percent) || percent <= 0 || percent > 10)
  )
    bad.push("percent");
  return bad;
}

function bodyOf(
  draft: Record<CardFeeKey, string>,
  sourcingOptionId: number | null | undefined,
): SourceCardFeeUpdate {
  const rule = draft.rule as Rule;
  const percent = number(draft.percent);
  const over = number(draft.overAmount);
  return {
    sourcing_option_id: sourcingOptionId ?? null,
    rule: rule === "unknown" ? null : rule,
    fee_percent: rule === "always" || rule === "over_amount" ? percent && String(percent) : null,
    fee_over_amount: rule === "over_amount" ? over && String(over) : null,
  };
}

function errorDetail(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

const DAY = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** Who told us and when: the supplier's person, or staff when set by hand. */
function toldLine(fee: SourceCardFee) {
  if (!fee.rule) return null;
  const when = fee.told_at ? DAY.format(new Date(fee.told_at)) : null;
  const who = fee.told_by ?? "Set by staff";
  return when ? `${who} · ${when}` : who;
}

type SaveCardFee = (
  body: SourceCardFeeUpdate,
  callbacks: { onSuccess: () => void; onError: (error: unknown) => void },
) => void;

/** The click-to-edit card fee fields, around whatever read view the page shows. */
function CardFeeEditor({
  fee,
  save,
  pending,
  editing,
  onEditingChange,
  children,
}: {
  fee: SourceCardFee;
  save: SaveCardFee;
  pending: boolean;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  children: ReactNode;
}) {
  return (
    <InlineFieldGroup
      label="card fee"
      fields={FIELDS}
      values={draftOf(fee)}
      invalid={invalidFields}
      saveLabel="Save card fee"
      labelled
      pending={pending}
      editing={editing}
      onEditingChange={onEditingChange}
      onSave={(draft) =>
        save(bodyOf(draft, fee.sourcing_option_id), {
          onSuccess: () => {
            toast.success("Card fee saved");
            onEditingChange(false);
          },
          onError: (error) =>
            toast.error(errorDetail(error, "Could not save the card fee")),
        })
      }
    >
      {children}
    </InlineFieldGroup>
  );
}

/**
 * Whether paying this source by card adds a fee (USA-1650). The email agents fill it in from
 * what the supplier tells us; staff can correct it here. A distributor's rule covers every
 * brand bought through it.
 */
export function CardFeeField({
  manufacturerUuid,
  fee,
}: {
  manufacturerUuid: string;
  fee: SourceCardFee;
}) {
  const update = useUpdateSourceCardFee(manufacturerUuid);
  const [editing, setEditing] = useState(false);
  const told = toldLine(fee);

  return (
    <div className="min-w-0 flex-1">
      <CardFeeEditor
        fee={fee}
        save={update.mutate}
        pending={update.isPending}
        editing={editing}
        onEditingChange={setEditing}
      >
        <span
          className={
            fee.rule
              ? "block text-sm font-medium"
              : "block text-sm text-muted-foreground"
          }
        >
          {fee.description}
        </span>
        {told && (
          <span
            className="block truncate text-xs text-muted-foreground"
            title={fee.supplier_wording ?? undefined}
          >
            {told}
          </span>
        )}
      </CardFeeEditor>
    </div>
  );
}

/**
 * The card fee line of an invoice whose AP vendor is linked to no manufacturer or supplier, so it
 * has no source to edit it on: click the line to change it. It opens across the Payment section.
 */
export function InvoiceCardFeeLine({
  invoiceUuid,
  fee,
}: {
  invoiceUuid: string;
  fee: SourceCardFee;
}) {
  const update = useUpdateInvoiceCardFee(invoiceUuid);
  const [editing, setEditing] = useState(false);

  return (
    <div
      className={cn(
        "border-b py-1.5 last:border-b-0",
        editing
          ? "space-y-1 sm:col-span-2"
          : "flex items-baseline justify-between gap-4",
      )}
    >
      <dt className="text-muted-foreground">Card fee</dt>
      <dd className={cn("min-w-0 font-medium tabular-nums", editing ? "px-2" : "text-right")}>
        <CardFeeEditor
          fee={fee}
          save={update.mutate}
          pending={update.isPending}
          editing={editing}
          onEditingChange={setEditing}
        >
          <span
            className={cn("block text-right", !fee.rule && "font-normal text-muted-foreground")}
            title={fee.supplier_wording ?? fee.source_name}
          >
            {fee.this_payment ?? fee.description}
          </span>
        </CardFeeEditor>
      </dd>
    </div>
  );
}
